// Navy header on one row: the mint SSG symbol, the mint + purple "pass"
// naming the agency you are working as, and your account button; it ends in
// the templates' pink curve. Tapping the pass switches agency without
// signing in again (spec 2.1).
import { CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useId, useState } from "react";
import { api, type Me } from "../lib/api.ts";
import { CurveEdge, Mark } from "./brand.tsx";
import { HelpButton, headerControlClass } from "./help.tsx";
import { openQuickFind } from "./quick-find.tsx";

/** The mint "pass" naming the agency you're working as; tap to switch agency or sign out. */
export function AgencyPass({
	me,
	className = "",
	inline = false,
}: {
	me: Me;
	className?: string;
	/** Open the menu in the flow (sidebar) rather than floating over the page (header). */
	inline?: boolean;
}) {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const active = me.agencies.find((a) => a.id === me.activeAgencyId);
	const others = me.agencies.filter((a) => a.id !== me.activeAgencyId);
	const listId = useId();

	const switchAgency = useMutation({
		mutationFn: (agencyId: string) => api("/session/agency", { body: { agencyId } }),
		onSuccess: async () => {
			setOpen(false);
			// Everything cached belongs to the old agency: reset it and refetch what is on screen.
			await queryClient.resetQueries();
			await navigate({ to: "/" });
		},
	});

	return (
		<div className={`relative min-w-0 ${className}`}>
			<button
				type="button"
				aria-expanded={open}
				aria-controls={listId}
				onClick={() => setOpen((v) => !v)}
				className="flex min-h-12 w-full items-center gap-2 rounded-2xl bg-mint px-3 py-1.5 text-left text-navy shadow-[inset_0_-4px_0_var(--color-purple)]"
			>
				<span className="min-w-0 flex-1">
					<span className="block text-sm font-bold tracking-[0.06em] uppercase">Working as</span>
					<span className="block truncate font-display text-base leading-tight font-bold">
						{active?.name ?? "Choose an agency"}
					</span>
				</span>
				<CaretDown weight="bold" size={20} aria-hidden="true" className={open ? "rotate-180" : ""} />
			</button>
			{open ? (
				<ul
					id={listId}
					className={`mt-2 overflow-hidden rounded-2xl bg-card text-ink ${inline ? "" : "absolute inset-x-0 z-30 min-w-64 shadow-[0_8px_24px_rgba(35,35,95,0.25)]"}`}
				>
					{others.map((a) => (
						<li key={a.id} className="border-b border-rule">
							<button
								type="button"
								onClick={() => switchAgency.mutate(a.id)}
								disabled={switchAgency.isPending}
								className="min-h-12 w-full px-4 text-left font-semibold"
							>
								Switch to {a.name}
							</button>
						</li>
					))}
					{me.isPlatformAdmin ? (
						<li className="border-b border-rule">
							<Link
								to="/platform"
								onClick={() => setOpen(false)}
								className="flex min-h-12 items-center px-4 font-semibold"
							>
								All agencies (SSG)
							</Link>
						</li>
					) : null}
					<li className="border-b border-rule">
						<Link
							to="/account"
							onClick={() => setOpen(false)}
							className="flex min-h-12 items-center px-4 font-semibold"
						>
							Your account
						</Link>
					</li>
					<li>
						<button
							type="button"
							onClick={async () => {
								const { authClient } = await import("../lib/auth-client.ts");
								await authClient.signOut();
								queryClient.clear();
								await navigate({ to: "/sign-in" });
							}}
							className="min-h-12 w-full px-4 text-left font-semibold text-link"
						>
							Sign out
						</button>
					</li>
				</ul>
			) : null}
		</div>
	);
}

/** Always visible while SSG staff are inside an agency. */
export function PlatformBanner({ me }: { me: Me }) {
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	if (!me.viaPlatform) return null;
	return (
		<div className="bg-[#15153a] text-mint" role="status">
			<div className="mx-auto flex max-w-[96rem] items-center justify-between gap-3 px-4 py-2 lg:px-8">
				<p className="text-sm font-semibold">You're viewing this agency as SSG. Every visit is recorded.</p>
				<button
					type="button"
					onClick={async () => {
						await api("/platform/leave", { body: {} });
						await queryClient.resetQueries();
						await navigate({ to: "/platform" });
					}}
					className="min-h-11 shrink-0 rounded-full border-2 border-mint px-4 text-sm font-bold"
				>
					Leave
				</button>
			</div>
		</div>
	);
}

/**
 * Phones and tablets: navy header with the symbol, the agency pass, Find, Help
 * and your account. Laptops use the sidebar. On phones your account is in the
 * pass menu and under More, so the agency name keeps its room.
 */
export function Header({ me }: { me: Me }) {
	return (
		<header className="on-navy pt-[env(safe-area-inset-top)] lg:hidden">
			<div className="bg-navy text-white">
				<div className="mx-auto flex max-w-[64rem] items-center gap-2 px-4 pt-3 pb-1 sm:gap-3">
					{/* On the narrowest phones the symbol gives way to the agency's name (Home is in the tab bar). */}
					<Link
						to="/"
						aria-label="Home"
						className="hidden min-h-12 shrink-0 content-center rounded-md text-mint min-[380px]:block"
					>
						<Mark size={36} />
					</Link>
					<AgencyPass me={me} className="flex-1 sm:max-w-96" />
					<span className="ml-auto flex gap-1 sm:gap-3">
						<button type="button" onClick={openQuickFind} className={headerControlClass}>
							<MagnifyingGlass size={22} weight="bold" aria-hidden="true" className="shrink-0" />
							<span>Find</span>
						</button>
						<HelpButton />
					</span>
					<Link
						to="/account"
						aria-label={`Your account, ${me.user.name}`}
						className="hidden size-12 shrink-0 items-center justify-center rounded-full border-2 border-mint font-display text-lg font-bold text-mint sm:inline-flex"
					>
						{me.user.name.trim().charAt(0).toUpperCase()}
					</Link>
				</div>
			</div>
			<CurveEdge />
		</header>
	);
}

/** Header when no agency is chosen yet (SSG staff, or someone picking an agency). */
export function SimpleHeader({ me }: { me: Me }) {
	return (
		<header className="on-navy pt-[env(safe-area-inset-top)]">
			<div className="bg-navy text-white">
				<div className="mx-auto flex max-w-[64rem] items-center gap-3 px-4 py-3 lg:max-w-[96rem] lg:px-8">
					<Link
						to={me.isPlatformAdmin ? "/platform" : "/choose-agency"}
						aria-label="Home"
						className="min-h-12 shrink-0 content-center rounded-md text-mint"
					>
						<Mark size={36} />
					</Link>
					<p className="flex-1 font-display text-lg font-bold">
						{me.isPlatformAdmin ? "SSG admin" : "Choose an agency"}
					</p>
					<HelpButton />
					<Link
						to="/account"
						aria-label={`Your account, ${me.user.name}`}
						className="inline-flex size-12 shrink-0 items-center justify-center rounded-full border-2 border-mint font-display text-lg font-bold text-mint"
					>
						{me.user.name.trim().charAt(0).toUpperCase()}
					</Link>
				</div>
			</div>
			<CurveEdge />
		</header>
	);
}
